// scanJobOutcome — what a watched scan upload's pipeline job has become, derived
// from its `document_jobs` row + per-page `document_extractions` rows, and how the
// „Zadania w tle” item describes it. Pure (no React, no data access) so every
// branch is unit-testable; ScanJobWatcher feeds it the rows and applies the result.

import type { TFunction } from "i18next";
import type { RowOf } from "@ds/config/db/graph";
import type { BatchItemStatus } from "@ds/actions/useProcesses";
import { isFailedPage } from "@shared/services/documents/pageFailure";

/** No job/page write for this long ⇒ the item stops waiting and says so. */
export const STALL_AFTER_MS = 15 * 60 * 1000;

// Job + its pages in ONE request: two separate reads can straddle the pipeline's
// page write and job write, showing a finished job with its pages still missing.
export const SCAN_JOB_SELECT =
  "id,status,error_message,page_count,updated_at,declared_type," +
  "pages:document_extractions(id,document_job_id,page_start,page_end,document_type,match_status,needs_review,review_notes,drive_file_id,updated_at," +
  "transaction:matched_transaction_id(reference_number))";

export type ScanJobRow = Pick<RowOf<"document_jobs">, "id" | "status" | "error_message" | "page_count" | "updated_at"> &
  Partial<Pick<RowOf<"document_jobs">, "declared_type">>;
export type ScanPageRow = Pick<
  RowOf<"document_extractions">,
  "id" | "document_job_id" | "page_start" | "page_end" | "document_type" | "match_status" | "needs_review" | "review_notes" | "drive_file_id" | "updated_at"
> & { transaction: { reference_number: string | null } | null };
/** A `SCAN_JOB_SELECT` row. */
export type ScanJobWithPages = ScanJobRow & { pages: ScanPageRow[] | null };

export interface ScanDocSummary {
  documentType: string;
  transactionRef: string | null;
}

export type ScanJobOutcome =
  | { kind: "processing"; donePages: number; totalPages: number | null }
  | { kind: "stalled"; minutes: number }
  | { kind: "done"; documents: ScanDocSummary[] }
  | { kind: "review"; documents: ScanDocSummary[]; reviewCount: number; documentCount: number }
  | {
      kind: "failed"; reason: string | null; okPages: number; totalPages: number;
      /** No page reached a transaction/Drive and none is still running — uploading again can't duplicate. */
      nothingFiled: boolean;
      /** A re-run is safe: nothing filed, and a declared-only type only when the job stored it. */
      retryable: boolean;
    };

const IN_FLIGHT = new Set(["pending", "processing", "splitting", "splitting_complete"]);
const FAILED = new Set(["failed", "splitting_failed"]);
// Whole-file customs documents and photos rerun through their declared profile
// only when the job stored it (`declared_type`, C11); jobs uploaded before that
// would be page-split / pushed through Gemini — never offer those.
const DECLARED_ONLY_TYPES = new Set(["ie599", "ie529", "waste_image"]);

const span = (p: ScanPageRow) => Math.max(1, p.page_end - p.page_start + 1);
const sumSpan = (rows: ScanPageRow[]) => rows.reduce((n, p) => n + span(p), 0);
const time = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);

function summarize(rows: ScanPageRow[]): ScanDocSummary[] {
  const seen = new Map<string, ScanDocSummary>();
  for (const p of rows) {
    const ref = p.transaction?.reference_number ?? null;
    const key = `${p.document_type}|${ref ?? ""}`;
    if (!seen.has(key)) seen.set(key, { documentType: p.document_type, transactionRef: ref });
  }
  return [...seen.values()];
}

export function deriveScanJobOutcome(args: {
  job: ScanJobRow | undefined;
  pages: ScanPageRow[];
  now: number;
  /** When the item started waiting (upload accepted, or the job was re-run). */
  since: number;
  stallAfterMs?: number;
}): ScanJobOutcome {
  const { job, now, since, stallAfterMs = STALL_AFTER_MS } = args;
  const pages = [...args.pages].sort((a, b) => a.page_start - b.page_start);
  const okRows = pages.filter((p) => p.match_status !== "pending" && !isFailedPage(p));

  if (!job || IN_FLIGHT.has(job.status)) {
    const lastActivity = Math.max(since, time(job?.updated_at), ...pages.map((p) => time(p.updated_at)));
    if (now - lastActivity > stallAfterMs) return { kind: "stalled", minutes: Math.floor(stallAfterMs / 60_000) };
    return {
      kind: "processing",
      donePages: sumSpan(pages.filter((p) => p.match_status !== "pending")),
      totalPages: job?.page_count ?? null,
    };
  }

  if (FAILED.has(job.status)) {
    // A still-pending page may yet be filed (its task can still run), and a re-run would
    // race that task — so only failed pages without a Drive file count as "nothing filed".
    const nothingFiled = pages.every((p) => isFailedPage(p) && !p.drive_file_id);
    return {
      kind: "failed",
      reason: job.error_message,
      okPages: sumSpan(okRows),
      totalPages: job.page_count ?? Math.max(sumSpan(pages), 1),
      nothingFiled,
      // Re-run re-files every page, so never when something was filed (duplicates).
      // A declared-only type still blocks the retry UNLESS the job itself recorded
      // its declared_type — then the rerun replays through that profile safely.
      retryable:
        nothingFiled &&
        (job.declared_type != null || pages.every((p) => !DECLARED_ONLY_TYPES.has(p.document_type))),
    };
  }

  if (job.status === "needs_review") {
    return {
      kind: "review",
      documents: summarize(okRows.filter((p) => !p.needs_review)),
      reviewCount: okRows.filter((p) => p.needs_review).length,
      documentCount: okRows.length,
    };
  }

  return { kind: "done", documents: summarize(okRows) };
}

const MAX_LISTED_DOCS = 4;

function formatDocs(docs: ScanDocSummary[], t: TFunction): string {
  const listed = docs.slice(0, MAX_LISTED_DOCS).map((d) => {
    const label = t(`pipeline.extractions.document_type.${d.documentType}`, d.documentType);
    return d.transactionRef ? `${label} → ${d.transactionRef}` : label;
  });
  const rest = docs.length - listed.length;
  return rest > 0 ? `${listed.join(", ")} (+${rest})` : listed.join(", ");
}

/** The item status + Polish message for an outcome. `canRetry`: the viewer may re-run the job. */
export function describeScanJobOutcome(
  outcome: ScanJobOutcome,
  t: TFunction,
  opts: { canRetry: boolean },
): { status: BatchItemStatus; message: string } {
  switch (outcome.kind) {
    case "processing":
      return {
        status: "processing",
        message: outcome.totalPages != null && outcome.totalPages > 1
          ? t("pipeline.tracking.processingPages", { done: outcome.donePages, total: outcome.totalPages })
          : t("pipeline.tracking.processing"),
      };
    case "stalled":
      return { status: "warning", message: t("pipeline.tracking.stalled", { minutes: outcome.minutes }) };
    case "done":
      return {
        status: "success",
        message: outcome.documents.length > 0
          ? t("pipeline.tracking.done", { docs: formatDocs(outcome.documents, t) })
          : t("pipeline.tracking.doneNoDocs"),
      };
    case "review":
      return {
        status: "warning",
        message: outcome.documents.length > 0
          ? t("pipeline.tracking.reviewWithDocs", {
              docs: formatDocs(outcome.documents, t), count: outcome.reviewCount, total: outcome.documentCount,
            })
          : t("pipeline.tracking.review", { count: outcome.reviewCount, total: outcome.documentCount }),
      };
    case "failed": {
      const reason = outcome.reason ?? t("pipeline.tracking.unknownReason");
      if (outcome.okPages > 0) {
        return {
          status: "error",
          message: t("pipeline.tracking.failedPartial", { reason, ok: outcome.okPages, total: outcome.totalPages }),
        };
      }
      // Nothing got in and this viewer can't (or mustn't) re-run it: uploading again is the fix.
      if (outcome.nothingFiled && !(outcome.retryable && opts.canRetry)) {
        return { status: "error", message: t("pipeline.tracking.failedReupload", { reason }) };
      }
      return { status: "error", message: t("pipeline.tracking.failed", { reason }) };
    }
  }
}
